import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/layout/Layout';
import api from '../lib/api';
import { toast } from 'sonner';
import { Plus, GitBranch, Check, X, Clock } from '@phosphor-icons/react';

export default function WorkflowsPage() {
  const [workflows, setWorkflows] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeTab, setActiveTab] = useState('workflows');

  useEffect(() => {
    fetchWorkflows();
    fetchPendingApprovals();
  }, []);

  const fetchWorkflows = async () => {
    try {
      const { data } = await api.get('/workflows');
      setWorkflows(data);
    } catch (error) {
      toast.error('Error al cargar workflows');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      const { data } = await api.get('/workflows/instances/my-pending');
      setPendingApprovals(data);
    } catch (error) {
      console.error('Error loading pending approvals', error);
    }
  };

  const handleApproval = async (instanceId, action, comments = '') => {
    try {
      await api.post(`/workflows/instances/${instanceId}/approve`, { action, comments });
      toast.success(action === 'approve' ? 'Aprobado exitosamente' : 'Rechazado');
      fetchPendingApprovals();
    } catch (error) {
      toast.error('Error al procesar aprobación');
    }
  };

  const CreateWorkflowModal = () => {
    const [formData, setFormData] = useState({
      name: '',
      description: '',
      entity_type: 'vacation',
      steps: [],
      is_active: true
    });

    const [stepForm, setStepForm] = useState({
      step_name: '',
      approver_roles: [],
      required: true
    });

    const addStep = () => {
      if (stepForm.step_name && stepForm.approver_roles.length > 0) {
        setFormData({
          ...formData,
          steps: [...formData.steps, stepForm]
        });
        setStepForm({ step_name: '', approver_roles: [], required: true });
      }
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        await api.post('/workflows', formData);
        toast.success('Workflow creado exitosamente');
        setShowCreateModal(false);
        fetchWorkflows();
      } catch (error) {
        toast.error('Error al crear workflow');
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-neutral-900 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <h3 className="text-xl font-bold text-white mb-4">Nuevo Workflow de Aprobación</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Nombre *</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-white"
                placeholder="ej: Aprobación de Vacaciones"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">Descripción</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows="2"
                className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-white"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">Tipo de Entidad *</label>
              <select
                value={formData.entity_type}
                onChange={(e) => setFormData({...formData, entity_type: e.target.value})}
                className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-white"
              >
                <option value="vacation">Vacaciones</option>
                <option value="purchase_order">Orden de Compra</option>
                <option value="invoice">Factura</option>
                <option value="payment">Pago</option>
                <option value="project">Proyecto</option>
              </select>
            </div>

            {/* Steps Configuration */}
            <div className="border-t border-neutral-800 pt-4">
              <h4 className="text-white font-semibold mb-3">Pasos de Aprobación</h4>
              <div className="space-y-2 mb-3">
                <input
                  type="text"
                  placeholder="Nombre del paso (ej: Aprobación Supervisor)"
                  value={stepForm.step_name}
                  onChange={(e) => setStepForm({...stepForm, step_name: e.target.value})}
                  className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-white text-sm"
                />
                <div className="flex gap-2">
                  <select
                    multiple
                    value={stepForm.approver_roles}
                    onChange={(e) => setStepForm({
                      ...stepForm,
                      approver_roles: Array.from(e.target.selectedOptions, option => option.value)
                    })}
                    className="flex-1 bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-white text-sm"
                  >
                    <option value="pm">PM</option>
                    <option value="gerencia">Gerencia</option>
                    <option value="admin">Admin</option>
                    <option value="rrhh">RRHH</option>
                  </select>
                  <button
                    type="button"
                    onClick={addStep}
                    className="px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                  >
                    Añadir Paso
                  </button>
                </div>
              </div>

              {formData.steps.length > 0 && (
                <div className="space-y-2">
                  {formData.steps.map((step, idx) => (
                    <div key={idx} className="bg-neutral-800/50 rounded-lg px-4 py-3">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-emerald-400 font-mono text-sm">Paso {idx + 1}</span>
                        <span className="text-white font-medium">{step.step_name}</span>
                      </div>
                      <div className="text-sm text-gray-400">
                        Aprobadores: {step.approver_roles.join(', ')}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-2 justify-end pt-4">
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 bg-neutral-800 text-white rounded-lg hover:bg-neutral-700"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={formData.steps.length === 0}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Crear Workflow
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <Layout title="Workflows de Aprobación">
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-white">Workflows de Aprobación</h1>
            <p className="text-gray-400 mt-1">Gestión de flujos de aprobación configurables</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700"
          >
            <Plus size={20} />
            Nuevo Workflow
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-neutral-800">
          <button
            onClick={() => setActiveTab('workflows')}
            className={`px-4 py-2 ${activeTab === 'workflows' ? 'border-b-2 border-emerald-500 text-white' : 'text-gray-400'}`}
          >
            Workflows
          </button>
          <button
            onClick={() => setActiveTab('pending')}
            className={`px-4 py-2 flex items-center gap-2 ${activeTab === 'pending' ? 'border-b-2 border-emerald-500 text-white' : 'text-gray-400'}`}
          >
            Aprobaciones Pendientes
            {pendingApprovals.length > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                {pendingApprovals.length}
              </span>
            )}
          </button>
        </div>

        {/* Workflows Tab */}
        {activeTab === 'workflows' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {loading ? (
              <div className="col-span-full text-center py-8 text-gray-400">Cargando...</div>
            ) : workflows.length === 0 ? (
              <div className="col-span-full text-center py-8 text-gray-400">
                No hay workflows configurados.
              </div>
            ) : (
              workflows.map((workflow) => (
                <div
                  key={workflow._id}
                  className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-6"
                >
                  <div className="flex items-start justify-between mb-4">
                    <GitBranch size={32} className="text-blue-400" />
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      workflow.is_active
                        ? 'bg-emerald-500/20 text-emerald-400'
                        : 'bg-gray-500/20 text-gray-400'
                    }`}>
                      {workflow.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>

                  <h3 className="text-lg font-bold text-white mb-1">{workflow.name}</h3>
                  <p className="text-sm text-gray-400 mb-4">{workflow.description}</p>

                  <div className="space-y-2">
                    <div className="text-xs text-gray-400">Tipo: <span className="text-white">{workflow.entity_type}</span></div>
                    <div className="text-xs text-gray-400">Pasos: <span className="text-white">{workflow.steps?.length || 0}</span></div>
                    
                    <div className="mt-4 space-y-1">
                      {workflow.steps?.map((step, idx) => (
                        <div key={idx} className="bg-neutral-800/50 rounded px-3 py-2 text-xs">
                          <span className="text-emerald-400">#{idx + 1}</span> {step.step_name}
                          <span className="text-gray-400 ml-2">→ {step.approver_roles?.join(', ')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Pending Approvals Tab */}
        {activeTab === 'pending' && (
          <div className="space-y-4">
            {pendingApprovals.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                No tienes aprobaciones pendientes.
              </div>
            ) : (
              pendingApprovals.map((instance) => {
                const currentStep = instance.steps[instance.current_step];
                return (
                  <div
                    key={instance._id}
                    className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-6"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-bold text-white mb-1">
                          {instance.entity_type} - {instance.entity_id}
                        </h3>
                        <p className="text-sm text-gray-400">Paso actual: {currentStep?.step_name}</p>
                      </div>
                      <Clock size={24} className="text-yellow-400" />
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => handleApproval(instance._id, 'approve')}
                        className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700"
                      >
                        <Check size={18} />
                        Aprobar
                      </button>
                      <button
                        onClick={() => handleApproval(instance._id, 'reject', 'Rechazado')}
                        className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
                      >
                        <X size={18} />
                        Rechazar
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {showCreateModal && <CreateWorkflowModal />}
    </Layout>
  );
}
